import { useTranslation } from "react-i18next";

import type { CcTransportEvent } from "@/api/code-companion";
import { useCcTransport } from "@/hooks/useCodeCompanion";

/**
 * A história de cada transporte, não o estado atual (S-064 onda 6).
 *
 * A leitura de decisão colapsa por transporte e devolve o último evento — correto para
 * decidir, e é o contrato do MCP. Para ler a entrega isso perde o essencial: "criado,
 * liberado, importação falhou" vira "falhou", que é a metade menos útil das três.
 *
 * Agrupado por transporte porque é como um Basis pensa: a pergunta é "o que aconteceu
 * com o HNIK900383", não "o que aconteceu às 17:04".
 */

/** Eventos por transporte, preservando a ordem que veio (mais recente primeiro). */
export function groupByTransport(
  events: CcTransportEvent[],
): Array<{ transport_id: string; events: CcTransportEvent[] }> {
  const ordem: string[] = [];
  const mapa = new Map<string, CcTransportEvent[]>();
  for (const e of events) {
    if (!mapa.has(e.transport_id)) {
      mapa.set(e.transport_id, []);
      ordem.push(e.transport_id);
    }
    mapa.get(e.transport_id)!.push(e);
  }
  return ordem.map((id) => ({ transport_id: id, events: mapa.get(id)! }));
}

export default function TransportHistory({
  projectId,
  packageId,
}: {
  projectId: string;
  packageId: number;
}) {
  const { t } = useTranslation();
  const { data } = useCcTransport(projectId, packageId);

  // Pacote que ainda não gerou transporte não precisa do bloco.
  if (!data?.items.length) return null;

  return (
    <div
      data-testid="transport-history"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="border-b border-border px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {t("Transports")}
      </div>
      <ul className="divide-y divide-border">
        {groupByTransport(data.items).map((g) => (
          <li key={g.transport_id} className="px-3 py-2">
            <div className="font-mono text-[11px] font-semibold">{g.transport_id}</div>
            <ul className="mt-0.5 space-y-0.5">
              {g.events.map((e, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-2 text-[11px]">
                  <span
                    className="font-mono text-[10px] font-semibold uppercase"
                    style={{ color: e.ok ? "#2b8f57" : "#b02a31" }}
                  >
                    {e.action}
                  </span>
                  {e.actor_type && (
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {e.actor_type}
                    </span>
                  )}
                  {e.message && <span className="break-words">{e.message}</span>}
                  <div className="flex-1" />
                  {e.recorded_at && (
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {new Date(e.recorded_at).toLocaleString()}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
